import CoverageDetailsModal from "components/modal/coverage-details/CoverageDetailsModal";
import PrintButton from "components/print-button/PrintButton";
import React from "react";
import { useLocation } from "react-router-dom";
import { ROUTES } from "routes/constants";
import { useProductConfig, useProductContext } from "context/ProductContext";
import { useQuote } from "hooks/useQuote";
import { getMLTADescription } from "utils/mltaQuoteUtils";
import { formatRegisteredSymbol } from "utils/textFormatting";
import { formatUSCurrency } from "utils/formatUSCurrency";
import { GIWLQuoteModal } from "components/modal/giwl-quote/GIWLQuoteModal";

interface ApplicationContainerProps {
    elementId?: string;
    isPrintable?: boolean;
    useTitle?: boolean;
    description?: string;
    children: React.ReactNode;
}

const ApplicationContainer = ({
    elementId,
    isPrintable = false,
    useTitle = true,
    description,
    children,
}: ApplicationContainerProps) => {
    // ========== Hooks ==========
    const { isDTO, isMLTA, isGIWL } = useProductContext();
    const { quoteData } = useQuote();
    const config = useProductConfig();
    const location = useLocation();

    // ========== Computed Values ==========
    // GIWL Edit Quote is only allowed on the Contact Information page (not payment,
    // review, or the decision pages, which all share this container).
    const isContactPage = location.pathname.includes(ROUTES.APPLICATION.CONTACT);

    const selectedCoverageAmount = quoteData?.selectedCoverageAmount ?? 0;
    const selectedCoveragePremium = quoteData?.selectedCoveragePremium ?? 0;

    // MLTA description (e.g., "Superior Family Coverage")
    const mltaDescription = getMLTADescription(quoteData?.selectedTier, quoteData?.selectedCoverageType);
    const displayDescription = isMLTA ? mltaDescription : description;

    // Formatted values
    const formattedCoverageAmount = selectedCoverageAmount ? formatUSCurrency(selectedCoverageAmount, "never") : undefined;
    const formattedCoveragePremium = selectedCoveragePremium
        ? `${formatUSCurrency(selectedCoveragePremium, "auto")}/month`
        : undefined;

    // CSS classes
    const hasCoverageValues = formattedCoverageAmount || formattedCoveragePremium;
    const mobileVisibility = isDTO ? "hidden" : "block";
    const titleFontStyle = hasCoverageValues ? "font-medium text-base leading-6" : "font-semibold text-4xl leading-10";
    const titleClassName = `application-container-title text-secondary font-sans tablet:block max-w-[364px] ${mobileVisibility} ${titleFontStyle}`;

    const tabletJustify = "tablet:justify-end";

    const title = formatRegisteredSymbol(config.displayName);

    // ========== Render ==========
    return (
        <div className="application-container-container w-full bg-base-content tablet:min-h-screen flex justify-center overflow-x-clip">
            <div className="w-full max-w-content tablet:grid tablet:grid-cols-5 flex flex-col">
                {/* Left Panel - Product Info */}
                {
                    <div
                        className={`application-container-left col-span-2 flex flex-col tablet:items-start items-center text-white
                            tablet:h-[calc(100vh-128px)] tablet:sticky tablet:top-[80px]
                            tablet:pt-12 tablet:pb-12 tablet:pl-28 p-4 justify-end ${tabletJustify}`}
                    >
                        <div
                            className={`application-container-content flex flex-col tablet:h-[${
                                isDTO ? "116px" : "166px"
                            }] tablet:gap-3 gap-2 tablet:items-start items-center`}
                        >
                            {elementId && isPrintable && (
                                <div className="application-container-print-button self-center tablet:self-start">
                                    <PrintButton elementId={elementId} />
                                </div>
                            )}

                            {useTitle && <p className={titleClassName}>{title}</p>}

                            {displayDescription && (
                                <p className="application-container-description text-secondary font-sans font-bold tablet:font-semibold text-lg tablet:text-4xl leading-6 tablet:leading-10 tablet:items-start items-center">
                                    {displayDescription}
                                </p>
                            )}

                            {formattedCoverageAmount && !isMLTA && (
                                <p className="application-container-coverage-amount text-secondary font-sans font-semibold text-3xl leading-9 text-center tablet:text-4xl tablet:leading-10 tablet:text-left">
                                    {formattedCoverageAmount}
                                </p>
                            )}

                            {formattedCoveragePremium && (
                                <p className="application-container-coverage-premium text-secondary font-sans font-bold text-lg leading-7 text-center tablet:font-semibold tablet:text-xl tablet:text-left">
                                    {formattedCoveragePremium}
                                </p>
                            )}

                            {isMLTA && formattedCoveragePremium && <CoverageDetailsModal />}
                            {isGIWL && isContactPage && <GIWLQuoteModal />}
                        </div>
                    </div>
                }

                {/* Right Panel - Content */}
                <div
                    id={elementId}
                    data-testid={elementId}
                    className="application-container-right col-span-3 bg-neutral pb-12 tablet:pt-12 pt-4 tablet:mr-24 flex justify-center items-start"
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

export default ApplicationContainer;
